<script setup lang="ts">
import { computed, ref, onBeforeUnmount } from 'vue';
import { z } from 'zod';
import {
  linuxPolicySchema,
  linuxRequestSchema,
  type LinuxPolicy,
  type LinuxRequest,
  type PrivilegeConnection,
} from '@atum-remote/protocol/linux-delegation';
import type { RemoteDevice, RemoteUiAdapter } from '../model.js';
const props = defineProps<{ device: RemoteDevice; adapter?: RemoteUiAdapter | undefined }>();
const policy = ref<LinuxPolicy>(),
  action = ref(''),
  reason = ref(''),
  busy = ref(false),
  error = ref('');
let approvalOrigin: string | undefined;
const stateSchema = z.object({
  id: z.uuid(),
  state: z.string(),
  expiresAt: z.string().optional(),
  approved: z.boolean().optional(),
  receipt: z.unknown().optional(),
  approvals: z
    .array(
      z.object({
        kind: z.enum(['operation', 'break_glass']),
        url: z
          .url()
          .refine(
            (url) =>
              new URL(url).origin === approvalOrigin &&
              !new URL(url).username &&
              !new URL(url).password &&
              new URL(url).pathname === '/approve',
          ),
      }),
    )
    .optional(),
});
const state = ref<z.infer<typeof stateSchema>>();
const pendingRequest = ref<LinuxRequest>();
const outcome = computed(() => {
  const result = z
    .object({ outcome: z.object({ state: z.string() }) })
    .safeParse(state.value?.receipt);
  return result.success ? result.data.outcome.state : undefined;
});
let connection: PrivilegeConnection | undefined,
  revision = 0;
onBeforeUnmount(() => {
  revision++;
  void connection?.close();
  connection = undefined;
});
async function run(operation: () => Promise<void>) {
  if (busy.value) return;
  busy.value = true;
  error.value = '';
  try {
    await operation();
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Operation unavailable.';
  } finally {
    busy.value = false;
  }
}
async function connect() {
  if (!props.adapter?.openPrivileges)
    throw new Error('Open this device in ATUM Remote web to approve service operations.');
  const attempt = revision;
  await connection?.close();
  connection = undefined;
  if (revision !== attempt) return;
  const opened = await props.adapter.openPrivileges(props.device);
  if (revision !== attempt) {
    await opened.close();
    return;
  }
  connection = opened;
  try {
    // The authenticated Remote catalog supplies the configured issuer origin.
    const catalog = await opened.request({ operation: 'catalog' });
    if (catalog.error) throw new Error(String(catalog.error));
    const origin = new URL(z.url().parse(catalog.approvalOrigin));
    if (
      origin.href !== origin.origin + '/' ||
      (origin.protocol !== 'https:' &&
        !(
          origin.protocol === 'http:' &&
          ['localhost', '127.0.0.1', '[::1]'].includes(origin.hostname)
        ))
    )
      throw new Error('Invalid approval origin.');
    const parsed = linuxPolicySchema.parse(catalog.policy);
    if (revision !== attempt) {
      await opened.close();
      return;
    }
    approvalOrigin = origin.origin;
    policy.value = parsed;
    action.value = parsed.actions[0]?.id ?? '';
  } catch (cause) {
    if (connection === opened) connection = undefined;
    await opened.close();
    throw cause;
  }
}
async function prepare() {
  if (!connection) throw new Error('Connect first.');
  // Creation can commit before its response is lost. Freeze the request's ID and
  // exact content so retrying recovers that same durable request and approval pair.
  pendingRequest.value ??= linuxRequestSchema.parse({
    id: crypto.randomUUID(),
    action_id: action.value,
    reason: reason.value.trim(),
  });
  const result = await connection.request({
    operation: 'prepare',
    request: pendingRequest.value,
  });
  if (result.error) throw new Error(String(result.error));
  state.value = stateSchema.parse(result);
}
async function update(operation: 'status' | 'execute' | 'cancel') {
  if (!connection || !state.value) return;
  const result = await connection.request({ operation, id: state.value.id });
  if (result.error) throw new Error(String(result.error));
  state.value = stateSchema.parse(result);
}
</script>
<template>
  <section
    v-if="!device.personalAccess && device.privileged?.available"
    class="panel privilege-panel"
  >
    <h2>Privileged service operation</h2>
    <p>
      Choose an operation installed in this server's policy. Approve its exact details with a
      security key or Bitwarden. Keep this session open while approving.
    </p>
    <button v-if="!policy" :disabled="busy" @click="run(connect)">
      Connect to service controls
    </button>
    <form v-else-if="!state" @submit.prevent="run(prepare)">
      <label for="privilege-operation">Operation</label>
      <select id="privilege-operation" v-model="action" :disabled="busy || !!pendingRequest">
        <option v-for="item in policy.actions" :key="item.id" :value="item.id">
          {{ item.operation }} {{ item.unit
          }}{{ item.break_glass ? ' (break-glass confirmation required)' : '' }}
        </option>
      </select>
      <label
        >Reason<textarea
          v-model="reason"
          required
          maxlength="512"
          :disabled="busy || !!pendingRequest"
        />
      </label>
      <button :disabled="busy || !action || !reason.trim()">
        {{ pendingRequest ? 'Retry approval request' : 'Request passkey approval' }}
      </button>
    </form>
    <template v-if="state">
      <p role="status">Request {{ state.id }} — {{ state.state }}</p>
      <p v-for="approval in state.approvals" :key="approval.url">
        <a :href="approval.url" target="_blank" rel="noopener noreferrer">{{
          approval.kind === 'break_glass'
            ? 'Review separate break-glass confirmation'
            : 'Review and approve with your passkey'
        }}</a>
      </p>
      <button :disabled="busy" @click="run(() => update('status'))">
        Check approval and receipt
      </button>
      <button
        v-if="state.state === 'pending'"
        :disabled="busy || !state.approved"
        @click="run(() => update('execute'))"
      >
        Execute approved operation once
      </button>
      <button
        v-if="state.state === 'pending'"
        :disabled="busy"
        @click="run(() => update('cancel'))"
      >
        Cancel request
      </button>
      <p v-if="outcome">Execution outcome: {{ outcome }}.</p>
      <pre v-if="state.receipt">{{ JSON.stringify(state.receipt, null, 2) }}</pre>
      <p
        v-if="outcome === 'uncertain' || ['uncertain', 'consuming', 'issued'].includes(state.state)"
      >
        Inspect the server and protected receipt journal before requesting another operation.
      </p>
    </template>
    <p v-if="error" role="alert">{{ error }}</p>
  </section>
</template>
<style scoped>
.privilege-panel {
  padding: 1.5rem;
}
label {
  display: grid;
  gap: 0.5rem;
  margin: 1rem 0;
}
pre {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
</style>
